// @ts-ignore
import { saveAs } from 'file-saver'
import JSZip from 'jszip'

import { useToastsContext } from '@fourthwall/components'
import { useMutation } from '@tanstack/react-query'

import { useCustomizationV4Query } from '../../hooks/useCustomizationV4Query'

const useDownloadZip = (customizationId: string) => {
  const { notify } = useToastsContext()
  const { customizationV4Query } = useCustomizationV4Query([customizationId])
  const links = customizationV4Query.data?.manufacturerFiles.map((file) => file.original)

  const downloadZipMutation = useMutation(async () => {
    const zip = new JSZip()

    if (!links) {
      throw new Error('useDownloadZip :: No links provided')
    }

    // Fetch all files concurrently
    const files = await Promise.all(
      links.map(async (link) => {
        try {
          const response = await fetch(link.uri)
          if (!response.ok) throw new Error(`Failed to fetch ${link.uri}`)
          const blob = await response.blob()
          const sizeName = link.tags.map((tag) => `${tag.name}_${tag.value}`).join('_')
          const fileExtensionMatch = link.uri.match(/\.([^./?#]+)(?:[?#]|$)/)
          if (!fileExtensionMatch) {
            throw new Error(`No file extension found in URI: ${link.uri}`)
          }
          const fileExtension = fileExtensionMatch[1]
          const fileName =
            sizeName.length > 0 ? `${sizeName}.${fileExtension}` : `artworkFile.${fileExtension}`

          return { fileName, blob }
        } catch (error) {
          notify(`Error fetching file ${link}`, { type: 'error' })
          throw error // Ensures Promise.all fails on errors
        }
      }),
    )

    files.forEach(({ fileName, blob }) => {
      zip.file(fileName, blob)
    })

    const content = await zip.generateAsync({ type: 'blob' })
    saveAs(content, 'artwork_files.zip')
  })

  return {
    downloadZip: downloadZipMutation.mutateAsync,
    isProcessing: downloadZipMutation.isLoading,
  }
}

export default useDownloadZip
